import React, { useMemo, useState } from "react";
import { FiUsers } from "react-icons/fi";
import ReusableTable from "../../../common/ReusableTable/ReusableTable";
import { EmptyState } from "../../../common/comms/EmptyState";
import { RetryBanner } from "../../../common/comms/RetryBanner";
import { BookingsTabs } from "./BookingsTabs";
import { COACH_MANAGER_DEPARTMENTS } from "./coachRoster";

const DEPARTMENT_TAB_PREFIX = "bk-dept-tab";

/**
 * The coach table, for a manager ("My team") and for a manager's manager.
 *
 * The two differ in breadth, not in shape, and neither has a read-only row:
 * every row is somebody on the caller's own reporting line, which is
 * `CallerScope.may_act_for`'s whole rule for another person's calendar. A
 * department peer with no link to the caller is deliberately absent rather than
 * listed and inert — the reporting tree does not return them at all.
 *
 * The Manager column is what makes an unexpected row explicable: a coach is here
 * because of the link named there, and somebody missing is somebody that link
 * has not been created for yet (on the Employees page).
 *
 * **Two layouts, and `departmentTabs` is the whole difference.** A manager's "My
 * team" stacks a card per department, because it already sits inside the My
 * calendar / My team strip and a second row of tabs under the first
 * reads as a broken hierarchy. Someone standing above the departments has no
 * strip of their own, so theirs puts the departments in one — one table at a
 * time, no card around the card, matching the workout-plans screen.
 *
 * Call types are not set here: they are a company-wide rule, on Diet Utils
 * (Nutritionist) and Workout Utils (Fitness) — see `CallTypesSection`.
 *
 * Fully presentational: the empty-state copy and the caption are props because
 * they are the parts that actually differ, and `ManagerBookings` owns
 * `useCoachRoster` because the tab strip above needs the count whichever tab is
 * open. Calling the hook here as well would fetch the reporting tree twice.
 *
 * @param {Object} props
 * @param {Array<Object>} props.coaches - Rows from `useCoachRoster`.
 * @param {boolean} props.loading
 * @param {Error|null} props.error
 * @param {() => void} props.refresh
 * @param {boolean} props.hasEmployeeProfile
 * @param {string} [props.emptyTitle] - Shown when there are no rows at all.
 * @param {string} [props.emptyHint]
 * @param {boolean} [props.showManager=false] - Include the Manager column.
 *   Worth it on a table spanning more than one team; on a manager's own team
 *   every row would read "You".
 * @param {boolean} [props.departmentTabs=false] - Put the departments in a tab
 *   strip and render one table, rather than stacking a card each.
 * @param {string} [props.caption] - One line under the tabs, in that layout.
 * @param {(coach: Object) => void} props.onSelectCoach - Called with the row's
 *   coach when it is activated.
 */
export const CoachRosterTab = ({
  coaches = [],
  loading = false,
  error = null,
  refresh,
  hasEmployeeProfile = true,
  emptyTitle = "No coaches are assigned to you yet",
  emptyHint = "Only coaches assigned to you directly appear here — a coach who reports to another manager is on their team, not yours. Assignments are made on the Employees page; sharing a department isn't enough on its own.",
  showManager = false,
  departmentTabs = false,
  caption,
  onSelectCoach,
}) => {
  /**
   * One section per coach department.
   *
   * There is no catch-all: `toCoachRows` only emits rows whose qualifying role
   * sits in one of these departments, so a bucket for anything else would be a
   * section that can never fill.
   */
  const sections = useMemo(() => {
    const byDepartment = new Map(COACH_MANAGER_DEPARTMENTS.map((title) => [title, []]));

    coaches.forEach((coach) => {
      byDepartment.get(coach.departmentTitle)?.push(coach);
    });

    return COACH_MANAGER_DEPARTMENTS.map((title) => ({
      key: title,
      heading: `${title} coaches`,
      rows: byDepartment.get(title) || [],
    })).filter((section) => section.rows.length > 0);
  }, [coaches]);

  // Which department tab is open. Held as "what was last picked" rather than
  // "what is shown", and resolved against the live sections below — a stored id
  // would go stale the moment a refresh empties that department, leaving a tab
  // selected that no longer exists and a panel with nothing in it.
  const [picked, setPicked] = useState(null);
  const activeSection =
    sections.find((section) => section.key === picked) || sections[0] || null;

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 240,
        minSize: 160,
        // The one reason a row might not do what it looks like it does, said
        // where someone reads it before clicking: a coach with no Employees row
        // has no calendar at all, since every booking call is keyed on
        // employee_id.
        Cell: ({ row }) => (
          <div className="flex flex-col gap-1">
            <span
              className={`text-sm font-semibold ${
                row.original.employeeId ? "text-bc-text" : "text-bc-faint"
              }`}
            >
              {row.original.name}
            </span>
            {!row.original.employeeId ? (
              <span className="text-[11px] text-bc-faint">Not verified — no calendar yet</span>
            ) : null}
          </div>
        ),
      },
      { accessorKey: "roleTitle", header: "Role", size: 150, minSize: 110 },
      // Which team this coach is on. Only on a table that spans more than one
      // — the roster is direct reports only now, so on a manager's own team
      // this column would say "You" on every row.
      ...(showManager
        ? [{ accessorKey: "managerName", header: "Manager", size: 180, minSize: 130 }]
        : []),
      {
        accessorKey: "slotsThisWeek",
        header: "Slots this week",
        size: 150,
        minSize: 120,
        // Null when the coach's department has no call types: there is no
        // number to show, which is not the same as zero.
        cell: ({ getValue }) => getValue() ?? "—",
      },
      { accessorKey: "callsToday", header: "Calls today", size: 130, minSize: 110 },
    ],
    [showManager]
  );

  // A coach with no Employees row has nothing to open — every booking call is
  // keyed on employee_id — so activating the row would lead to a dead screen.
  const handleRowClick = (coach) => {
    if (!coach?.employeeId) return;
    onSelectCoach?.(coach);
  };

  const renderTable = (rows) => (
    <ReusableTable
      columns={columns}
      data={rows}
      enableSorting
      // Off for a handful of linked coaches, where the header stays clean and
      // scanning is faster than filtering. On for a long list, where finding one
      // person by name is the first thing anyone does. (ReusableTable has no
      // pagination — `enablePagination` is accepted and ignored — so sorting and
      // filtering are the only levers a long list gets.)
      enableFiltering={rows.length > 10}
      enableResizing
      enablePagination={false}
      onRowClick={handleRowClick}
      emptyMessage={emptyTitle}
    />
  );

  if (!hasEmployeeProfile) {
    return (
      <EmptyState
        title="You don't have an employee profile"
        hint="Coach assignments hang off an employee record. Ask an admin to verify your account, then reload."
      />
    );
  }

  const count = coaches.length;

  return (
    <div className="space-y-5">
      {error ? (
        <RetryBanner
          message={error.message || "Something went wrong loading your coaches."}
          onRetry={refresh}
        />
      ) : null}

      {loading ? (
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <p className="py-8 text-center text-sm text-bc-soft">Loading your coaches…</p>
        </section>
      ) : count === 0 ? (
        // The likeliest "is this broken?" state, so it says why rather than
        // showing an empty table. What "why" means differs by caller, which is
        // what the copy props are for.
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <EmptyState
            icon={<FiUsers className="mx-auto h-12 w-12 text-gray-400" />}
            title={emptyTitle}
            hint={emptyHint}
          />
        </section>
      ) : departmentTabs ? (
        <div className="space-y-3">
          {/*
            One department, one heading. A tablist you cannot leave is not a
            control, so the strip only appears once there is somewhere to go —
            but the department still has to be named, or the table claims to be
            everyone the caller reaches when it is really half of them.
          */}
          {sections.length > 1 ? (
            <BookingsTabs
              value={activeSection.key}
              onChange={setPicked}
              items={sections.map((section) => ({
                id: section.key,
                label: section.key,
                count: section.rows.length,
              }))}
              label="Coach departments"
              idPrefix={DEPARTMENT_TAB_PREFIX}
            />
          ) : (
            <h2 className="text-base font-semibold text-bc-text">
              {activeSection.heading}
            </h2>
          )}

          {caption ? <p className="text-sm text-bc-soft">{caption}</p> : null}

          <div
            {...(sections.length > 1
              ? {
                  role: "tabpanel",
                  id: `${DEPARTMENT_TAB_PREFIX}-${activeSection.key}-panel`,
                  "aria-labelledby": `${DEPARTMENT_TAB_PREFIX}-${activeSection.key}`,
                }
              : null)}
          >
            {renderTable(activeSection.rows)}
          </div>
        </div>
      ) : (
        sections.map((section) => (
          <section key={section.key} className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 className="text-base font-semibold text-bc-text">{section.heading}</h2>
              <p className="text-xs text-bc-soft">
                {section.rows.length} coach{section.rows.length === 1 ? "" : "es"} · select a
                coach to open their calendar
              </p>
            </div>

            <div className="mt-4">{renderTable(section.rows)}</div>
          </section>
        ))
      )}
    </div>
  );
};
